/**
 * VoiceInterface - Handles voice call UI and functionality
 * Part of the unified TTPChatWidget
 * Optimized for Wix iframe embedding
 */

import { AgentSDK } from '../index.js';
import { checkPermissionState, getDeviceInfo } from '../shared/MicPermissionUtils.js';
import { showPrePromptModal, showBlockedModal, showNoMicrophoneModal } from '../shared/MicPermissionModals.js';
import { Templates } from './voice/templates.js';
import { Styles } from './voice/styles.js';
import { Transcript } from './voice/transcript.js';
import { TextHandlers } from './voice/textHandlers.js';
import { Handlers } from './voice/handlers.js';
import { Desktop } from './voice/desktop.js';
import { Mobile } from './voice/mobile.js';

export class VoiceInterface {
  constructor(config) {
    this.config = config;
    this.audioStream = null;
    this.callStartTime = null;
    this.callTimerInterval = null;
    this.sdk = null;
    this.isActive = false;
    this.isShowingDomainError = false; // Flag to track if domain error is being displayed
    // Shadow root reference for DOM queries
    this.shadowRoot = config.shadowRoot || document;
    // Text SDK reference for sending text messages during voice calls
    this.textSDK = config.textSDK || null;
    // Mobile detection
    this.deviceInfo = getDeviceInfo();
    this.isMobile = this.deviceInfo.isMobile;
    // Conversation messages for mobile UI (owned by mobile module, initialized there)
    // Desktop conversation state
    this.conversationExpanded = false;
    // Mic and speaker mute states
    this.isMicMuted = false;
    this.isSpeakerMuted = false;
    
    // Initialize AgentSDK for voice connection
    // Pass through output format configuration if provided
    // Get visualAssistant from config or agentSettingsOverride (same pattern as VoiceSDK)
    const visualAssistant = config.visualAssistant || config.agentSettingsOverride?.visualAssistant;
    
    this.sdk = new AgentSDK({
      agentId: config.agentId,
      appId: config.appId,
      signedUrl: config.signedUrl, // Pass through signedUrl if provided
      agentSettingsOverride: config.agentSettingsOverride, // Pass through agentSettingsOverride if provided
      visualAssistant: visualAssistant, // Pass through visualAssistant config (with fallback to agentSettingsOverride)
      variables: config.variables || {},
      language: config.language || 'en',
      // Output format configuration (defaults to 44100 Hz, 16-bit, PCM, raw)
      outputContainer: config.outputContainer,
      outputEncoding: config.outputEncoding,
      outputSampleRate: config.outputSampleRate,
      outputChannels: config.outputChannels,
      outputBitDepth: config.outputBitDepth,
      // Pass event callbacks to AgentSDK
      onConversationStart: config.onConversationStart,
      onConversationEnd: config.onConversationEnd,
      onBargeIn: config.onBargeIn,
      onAudioStartPlaying: config.onAudioStartPlaying,
      onAudioStoppedPlaying: config.onAudioStoppedPlaying,
      onSubtitleDisplay: config.onSubtitleDisplay
    });
    
    // Initialize modules with context (delegation pattern)
    this.templates = new Templates(this);
    this.styles = new Styles(this);
    this.transcript = new Transcript(this);
    this.textHandlers = new TextHandlers(this);
    this.handlers = new Handlers(this);
    this.desktop = new Desktop(this);
    this.mobile = new Mobile(this);
    
    // Setup SDK event handlers
    this.setupSDKEventHandlers();
    
    // Check for resume state on initialization
    this.checkAndHandleResume();
  }

  /**
   * Get the underlying VoiceSDK instance (if available)
   * @returns {VoiceSDK_v2|null} The VoiceSDK instance or null if not available
   */
  getVoiceSDK() {
    return this.sdk?.voiceSDK || null;
  }
  
  /**
   * Check for resume state and handle auto-resume if needed
   * This should be called after SDK initialization
   * @returns {Object|null} Resume info if resuming, null otherwise
   */
  checkAndHandleResume() {
    // If we already checked and stored resume info, return it
    // Use !== undefined to distinguish between "not checked" and "checked, no resume info"
    if (this._resumeInfo !== undefined) {
      console.log('🔄 [VOICE INTERFACE] Returning cached resume info:', this._resumeInfo);
      return this._resumeInfo;
    }
    
    // Check for resume state using AgentSDK
    const resumeInfo = this.sdk.checkResumeBeforeConnect();
    
    // Store the result (even if null) to prevent checking again
    this._resumeInfo = resumeInfo;
    
    if (resumeInfo) {
      console.log('🔄 [WIDGET] Auto-resuming call:', resumeInfo);
      console.log('   From:', resumeInfo.fromPage, '→ To:', resumeInfo.toPage);
      
      // Trigger resume callback if configured
      if (this.config.onResume) {
        this.config.onResume(resumeInfo);
      }
      
      return resumeInfo;
    }
    
    return null;
  }
  
  /**
   * Setup event handlers for AgentSDK
   */
  setupSDKEventHandlers() {
    // Handle transcript updates
    this.sdk.onTranscript = (text) => {
      console.log('📝 [VOICE INTERFACE] onTranscript called with text:', text);
      this.updateTranscript(text);
    };
    
    // Handle playback started - update status to "listening" when audio actually plays
    this.sdk.onPlaybackStarted = () => {
      console.log('🔊 [VOICE INTERFACE] Playback started - updating status to listening');
      if (this.isMobile) {
        this.updateMobileStatus('listening');
      } else {
        // Desktop: update status to listening
        this.updateDesktopStatus();
      }
    };
    
    // Handle agent speaking state
    this.sdk.onAgentSpeaking = (isStart) => {
      const avatar = this.shadowRoot.getElementById('voiceAvatarActive');
      if (avatar) {
        if (isStart) {
          avatar.classList.add('speaking');
          // Update mobile status
          if (this.isMobile) {
            this.updateMobileStatus('speaking');
            // Waveform should already be animating, but ensure it is
            this.startWaveformAnimation();
          } else {
            // Desktop: update status and waveform
            this.updateDesktopStatus();
            this.startDesktopWaveformAnimation();
          }
        } else {
          avatar.classList.remove('speaking');
          // Update mobile status
          if (this.isMobile) {
            this.updateMobileStatus('listening');
            // Keep waveform animating when listening
            this.startWaveformAnimation();
          } else {
            // Desktop: update status and waveform
            this.updateDesktopStatus();
            this.startDesktopWaveformAnimation();
          }
        }
      }
    };
    
    // Handle websocket disconnection - hangup the call
    this.sdk.onDisconnected = async (event) => {
      console.log('🔌 WebSocket disconnected, hanging up call...');
      
      // Check if disconnect was due to domain error/server rejection
      // Check both code 1008 and reason text for domain-related errors
      const isDomainError = event && (
        (event.code === 1008 && event.reason && 
         (event.reason.includes('Domain not whitelisted') || 
          event.reason.includes('domain') || 
          event.reason.includes('whitelist'))) ||
        (event.code === 1008 && event.reason && event.reason.includes('localhost'))
      );
      
      if (isDomainError) {
        // Domain error - log it and show error message in widget
        console.error('❌ Domain not whitelisted - WebSocket closed:', event.code, event.reason);
        // CRITICAL: Stop audio capture/VAD if it has started and show domain error
        await this.endCallOnServerRejection();
      } else if (this.isActive) {
        // Normal disconnect - end call gracefully
        // Set isActive to false first to prevent re-entry
        this.isActive = false;
        this.endVoiceCall();
      }
    };
    
    // Handle errors
    this.sdk.onError = async (error) => {
      // Check if it's a domain error FIRST (before checking server rejection)
      const isDomainError = error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
          (error.message && error.message.includes('Domain not whitelisted')));
      
      if (isDomainError) {
        // Domain error - log it and show error message in widget
        console.error('❌ Voice SDK Error: Domain not whitelisted:', error);
        // CRITICAL: Stop audio capture/VAD if it has started
        await this.endCallOnServerRejection();
        return;
      }
      
      // Handle other server rejections gracefully (don't log as error)
      if (error.isServerRejection || error.name === 'ServerRejected') {
        // Server rejection is expected - don't log as error
        // The domain error handler will show the appropriate message
        return;
      }
      
      console.error('❌ Voice SDK Error:', error);
      this.showError(error.message || error);
    };
  }
  
  /**
   * Setup text inject acknowledgment handler
   * Delegates to textHandlers module
   */
  setupTextInjectAckHandler() {
    return this.textHandlers.setupTextInjectAckHandler();
  }

  /**
   * Handle text inject acknowledgment
   * Delegates to textHandlers module
   */
  handleTextInjectAck(data) {
    return this.textHandlers.handleTextInjectAck(data);
  }

  /**
   * Send desktop text message
   * Delegates to textHandlers module
   */
  sendDesktopTextMessage() {
    return this.textHandlers.sendDesktopTextMessage();
  }

  /**
   * Send mobile text message
   * Delegates to textHandlers module
   */
  sendMobileTextMessage() {
    return this.textHandlers.sendMobileTextMessage();
  }

  /**
   * Helper function to get translated text
   */
  t(key) {
    const lang = this.config.language || 'en';
    const translations = this.config.translations?.[lang] || this.config.translations?.en || {};
    return translations[key] || key;
  }

  /**
   * Get text with fallback: direct config > translations > key
   */
  getText(directConfigKey, translationKey) {
    // First check for direct configuration (bypasses translation system)
    if (this.config[directConfigKey] !== undefined && this.config[directConfigKey] !== null) {
      return this.config[directConfigKey];
    }
    // Fall back to translation system
    return this.t(translationKey);
  }

  getTooltip(key) {
    const tooltip = this.config.tooltips?.[key];
    if (tooltip !== null && tooltip !== undefined) return tooltip;
    // Use translations for default tooltips
    const defaults = {
      mute: this.t('mute'),
      speaker: this.t('speaker'),
      endCall: this.t('endCall')
    };
    return defaults[key] || '';
  }

  /**
   * Update all timer elements with the given time text
   * Always updates both desktop and compact timers to ensure consistency
   */
  _updateTimerElements(timerText, elapsed) {
    // Update legacy desktop timer if it exists
    const timerEl = this.shadowRoot.getElementById('voiceTimer');
    if (timerEl) {
      timerEl.textContent = timerText;
    }

    // Always update BOTH timer elements - the visibility optimization was causing bugs
    // where the collapsed view timer wasn't updating
    const desktopTimerText = this.shadowRoot.getElementById('desktopTimerText');
    const compactTimerText = this.shadowRoot.getElementById('compactTimerText');

    if (desktopTimerText) {
      desktopTimerText.textContent = timerText;
    }
    if (compactTimerText) {
      compactTimerText.textContent = timerText;
    }

    // Debug logging (only in first 2 seconds)
    if (!compactTimerText && !desktopTimerText && elapsed < 2000) {
      console.warn('⚠️ Timer elements not found - compactTimerText:', !!compactTimerText, 'desktopTimerText:', !!desktopTimerText, 'elapsed:', elapsed);
      const allTimers = this.shadowRoot.querySelectorAll('[id*="Timer"], [id*="timer"]');
      console.log('Found timer-like elements:', Array.from(allTimers).map(el => el.id));
    }

    // Update mobile duration badge
    if (this.isMobile) {
      const mobileDurationText = document.getElementById('mobileDurationText');
      const mobileHeaderDuration = document.getElementById('mobileHeaderDuration');
      if (mobileDurationText) mobileDurationText.textContent = timerText;
      if (mobileHeaderDuration) mobileHeaderDuration.textContent = timerText;
    }
  }

  /**
   * Generate HTML for voice interface
   * Delegates to templates module
   */
  generateHTML() {
    return this.templates.generateHTML();
  }

  /**
   * Generate mobile-specific HTML for minimized voice call UI
   * Delegates to templates module
   */
  generateMobileHTML() {
    return this.templates.generateMobileHTML();
  }

  /**
   * Generate CSS for voice interface
   * Delegates to styles module
   */
  generateCSS() {
    return this.styles.generateCSS();
  }

  /**
   * Setup event handlers for voice interface
   * Delegates to handlers module
   */
  setupEventHandlers(callbacks) {
    return this.handlers.setupEventHandlers(callbacks);
  }

  /**
   * Setup mobile-specific event handlers
   * Delegates to handlers module
   */
  setupMobileEventHandlers() {
    return this.handlers.setupMobileEventHandlers();
  }

  /**
   * Adjust UI sizes based on container height
   * Delegates to desktop module
   */
  adjustSizesForContainer() {
    return this.desktop.adjustSizesForContainer();
  }

  /**
   * Start desktop waveform animation
   * Delegates to desktop module
   */
  startDesktopWaveformAnimation() {
    return this.desktop.startDesktopWaveformAnimation();
  }

  /**
   * Stop desktop waveform animation
   * Delegates to desktop module
   */
  stopDesktopWaveformAnimation() {
    return this.desktop.stopDesktopWaveformAnimation();
  }

  /**
   * Update desktop status display
   * Delegates to desktop module
   */
  updateDesktopStatus() {
    return this.desktop.updateDesktopStatus();
  }

  /**
   * Update live waveform visualizer
   * Delegates to desktop module
   */
  updateLiveWaveform(isActive) {
    return this.desktop.updateLiveWaveform(isActive);
  }

  /**
   * Show error message
   * Delegates to desktop module
   */
  showError(message) {
    return this.desktop.showError(message);
  }

  /**
   * Show domain validation error
   * Delegates to desktop module
   */
  showDomainError() {
    return this.desktop.showDomainError();
  }


  /**
   * Start voice call with enhanced permission handling
   */
  async startVoiceCall() {
    if (this.isActive) {
      console.log('⚠️ Voice call already active');
      return;
    }
    
    console.log('🎤 Starting voice call...');
    
    // Check permission state first
    const permissionState = await checkPermissionState();
    console.log(`📋 Current permission state: ${permissionState}`);
    
    // If permission is explicitly denied, show blocked modal
    if (permissionState === 'denied') {
      console.log('🚫 Permission is denied, showing blocked modal...');
      // Reset connecting state since we're not proceeding
      this.resetConnectingState();
      const deviceInfo = getDeviceInfo();
      showBlockedModal(
        () => {
          // Modal closed - user can try again
          console.log('Blocked modal closed');
        },
        () => {
          // User clicked refresh
          window.location.reload();
        }
      );
      // Throw error so caller knows call didn't start
      throw new Error('Microphone permission denied');
    }
    
    // If permission is in 'prompt' state, show pre-prompt modal
    if (permissionState === 'prompt' || permissionState === 'unknown') {
      console.log('📋 Permission is in prompt state, showing pre-prompt modal...');
      return new Promise((resolve, reject) => {
        showPrePromptModal(
          async () => {
            // User accepted pre-prompt - proceed with voice call
            console.log('✅ Pre-prompt accepted, proceeding with voice call...');
            try {
              await this.proceedWithVoiceCall();
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          () => {
            // User cancelled pre-prompt - reset connecting state
            console.log('❌ Pre-prompt cancelled');
            this.resetConnectingState();
            // Reject so caller knows call was cancelled
            reject(new Error('Call cancelled by user'));
          }
        );
      });
    }
    
    // Permission is granted or unknown - proceed directly
    await this.proceedWithVoiceCall();
  }

  /**
   * Proceed with voice call after permission check/pre-prompt
   */
  async proceedWithVoiceCall() {
    try {
      // Clear conversation history for new calls (but preserve it for resume calls)
      const isResumeCall = this._resumeInfo !== null && this._resumeInfo !== undefined;
      if (!isResumeCall) {
        console.log('🆕 New call detected - clearing conversation history');
        this.transcript.clearConversationMessages();
        
        // Collapse conversation history panel for new calls
        this.conversationExpanded = false;
        const panel = this.shadowRoot.getElementById('desktopConversationPanel');
        const header = this.shadowRoot.getElementById('conversationHeader');
        const toggleText = this.shadowRoot.getElementById('conversationToggleText');
        const voiceInterface = this.shadowRoot.querySelector('.voice-interface');
        const originalSection = this.shadowRoot.getElementById('desktopVoiceSection');
        const compactSection = this.shadowRoot.getElementById('compactVoiceSection');
        
        if (panel) {
          panel.classList.remove('expanded');
          panel.classList.add('collapsed');
        }
        if (header) {
          header.classList.remove('expanded');
        }
        if (toggleText) {
          toggleText.textContent = 'Show history';
        }
        if (voiceInterface) {
          voiceInterface.classList.remove('expanded');
        }
        
        // Show original layout, hide compact layout
        if (originalSection) {
          originalSection.style.display = '';
        }
        if (compactSection) {
          compactSection.style.display = 'none';
        }
      } else {
        console.log('🔄 Resume call detected - preserving conversation history');
      }
      
      // First, handle UI state changes (but don't create mobile bar yet)
      if (this.isMobile) {
        // On mobile, hide widget panel immediately (but keep floating button visible)
        // We'll hide the floating button AFTER permission is granted
        const idleState = this.shadowRoot.getElementById('voiceIdleState');
        if (idleState) {
          idleState.style.display = 'none';
        }
        
        // Hide widget panel but keep floating button visible
        const panel = this.shadowRoot.getElementById('text-chat-panel');
        if (panel) {
          panel.classList.remove('open');
          panel.style.display = 'none';
          console.log('✅ Widget panel hidden (waiting for permission)');
        } else {
          const fallbackPanel = document.getElementById('text-chat-panel');
          if (fallbackPanel) {
            fallbackPanel.classList.remove('open');
            fallbackPanel.style.display = 'none';
            console.log('✅ Widget panel hidden (waiting for permission, fallback)');
          }
        }
        
        // CRITICAL for mobile: Request permission BEFORE connecting to WebSocket
        // This prevents the agent from starting to speak before permission is granted
        console.log('📱 Mobile device - requesting mic permission before connecting...');
        try {
          const sampleRate = this.config.outputSampleRate || 44100;
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              sampleRate: sampleRate,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          });
          
          // Stop the stream immediately - we just wanted permission
          // We'll get a new stream when startListening() is called
          mediaStream.getTracks().forEach(track => track.stop());
          
          console.log('✅ Mobile: Microphone permission granted - proceeding with connection');
          
          // NOW hide the floating button too (permission granted)
          const floatingButton = this.shadowRoot.getElementById('text-chat-button');
          if (floatingButton) {
            floatingButton.style.display = 'none';
            console.log('✅ Floating button hidden (permission granted)');
          } else {
            const fallbackButton = document.getElementById('text-chat-button');
            if (fallbackButton) {
              fallbackButton.style.display = 'none';
              console.log('✅ Floating button hidden (permission granted, fallback)');
            }
          }
        } catch (error) {
          console.error('❌ Mobile: Permission denied before connecting:', error);
          // Permission was denied - reset connecting state and restore UI
          this.resetConnectingState();
          
          // Remove mobile bar if it was created early
          const existingBar = document.getElementById('mobile-voice-call-bar-container');
          if (existingBar) {
            this.removeMobileMinimizedBar();
          }
          
          // Restore floating button visibility
          const floatingButton = this.shadowRoot.getElementById('text-chat-button');
          if (floatingButton) {
            floatingButton.style.display = '';
          } else {
            const fallbackButton = document.getElementById('text-chat-button');
            if (fallbackButton) {
              fallbackButton.style.display = '';
            }
          }
          
          const idleState = this.shadowRoot.getElementById('voiceIdleState');
          if (idleState) {
            idleState.style.display = 'flex';
          }
          
          // Restore widget panel visibility
          const panel = this.shadowRoot.getElementById('text-chat-panel');
          if (panel) {
            panel.style.display = '';
          } else {
            const fallbackPanel = document.getElementById('text-chat-panel');
            if (fallbackPanel) {
              fallbackPanel.style.display = '';
            }
          }
          
          // Show blocked modal
          const deviceInfo = getDeviceInfo();
          showBlockedModal(
            () => {
              this.resetUIState();
            },
            () => {
              window.location.reload();
            }
          );
          return; // Don't proceed with connection
        }
      } else {
        // Desktop: Reset connecting state, then hide idle state and show active state
        this.resetConnectingState();
        const idleState = this.shadowRoot.getElementById('voiceIdleState');
        const activeState = this.shadowRoot.getElementById('voiceActiveState');
        
        if (idleState) idleState.style.display = 'none';
        if (activeState) activeState.style.display = 'flex';
      }
      
      const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
      
      // Adjust sizes based on container dimensions
      setTimeout(() => this.adjustSizesForContainer(), 50);
      
      // Prevent any scrolling when switching states
      if (voiceInterface) {
        voiceInterface.scrollTop = 0;
      }
      
      // Get signed URL and connect
      // On mobile, permission is already granted at this point
      // Get signed URL from config or construct it
      const signedUrl = this.config.signedUrl || (this.config.agentId && this.config.appId 
        ? `wss://speech.talktopc.com/ws/conv?agentId=${this.config.agentId}&appId=${this.config.appId}` 
        : null);
      
      if (!signedUrl) {
        throw new Error('signedUrl or agentId+appId required for voice calls');
      }
      console.log('🔗 Got signed URL:', signedUrl);
      
      // If SDK is already connected, disconnect first to ensure clean state
      // This is important for retrying after permission errors
      if (this.sdk && this.sdk.isConnected) {
        console.log('🔄 SDK already connected, disconnecting for clean retry...');
        try {
          this.sdk.disconnect();
          // Wait a bit for disconnect to complete
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (disconnectError) {
          console.warn('Warning: Error disconnecting existing connection:', disconnectError);
        }
      }
      
      const connected = await this.sdk.connect(signedUrl);
      console.log('✅ Connected to WebSocket, connected:', connected);
      
      // Set up text inject ack handler using EventEmitter pattern
      this.setupTextInjectAckHandler();
      
      // Track if server rejected the connection
      let serverRejected = false;
      const originalOnError = this.sdk.onError;
      const originalOnDisconnected = this.sdk.onDisconnected;
      
      // Temporarily override error handlers to catch server rejection before starting audio
      this.sdk.onError = (error) => {
        if (error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
            (error.message && error.message.includes('Domain not whitelisted')))) {
          serverRejected = true;
          console.log('🚫 Server rejected connection before starting audio capture');
        }
        // Call original handler
        if (originalOnError) originalOnError.call(this.sdk, error);
      };
      
      this.sdk.onDisconnected = (event) => {
        // Check if disconnect was due to domain error
        if (event && event.code === 1008 && event.reason && 
            (event.reason.includes('Domain not whitelisted') || 
             event.reason.includes('domain') || 
             event.reason.includes('whitelist'))) {
          serverRejected = true;
          console.log('🚫 Server disconnected due to domain error before starting audio capture');
        }
        // Call original handler
        if (originalOnDisconnected) originalOnDisconnected.call(this.sdk, event);
      };
      
      // Wait for connection to be fully ready (for hello message exchange)
      // Check both AgentSDK.isConnected and underlying VoiceSDK connection
      let attempts = 0;
      while (attempts < 50) {
        // Check if server rejected
        if (serverRejected) {
          console.log('🚫 Server rejected connection, aborting audio capture');
          throw new Error('DOMAIN_NOT_WHITELISTED');
        }
        
        // Check AgentSDK flag
        if (this.sdk.isConnected) {
          // Also verify underlying WebSocket is actually ready
          if (this.sdk.voiceSDK && this.sdk.voiceSDK.isConnected && 
              this.sdk.voiceSDK.websocket && this.sdk.voiceSDK.websocket.readyState === WebSocket.OPEN) {
            // Connection is ready (hello_ack received) - keep status as "connecting" until audio actually plays
            // Status will be updated to "listening" when playbackStarted event fires
            break;
          }
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      // Final check - ensure WebSocket is actually OPEN and server didn't reject
      if (serverRejected) {
        // Restore original handlers before throwing
        this.sdk.onError = originalOnError;
        this.sdk.onDisconnected = originalOnDisconnected;
        console.log('🚫 Server rejected connection, aborting audio capture');
        throw new Error('DOMAIN_NOT_WHITELISTED');
      }
      
      // Check if connection is still valid before starting audio
      if (!this.sdk.isConnected || 
          !this.sdk.voiceSDK || 
          !this.sdk.voiceSDK.isConnected || 
          !this.sdk.voiceSDK.websocket || 
          this.sdk.voiceSDK.websocket.readyState !== WebSocket.OPEN) {
        // Restore original handlers before throwing
        this.sdk.onError = originalOnError;
        this.sdk.onDisconnected = originalOnDisconnected;
        console.warn('⚠️ Connection lost before starting audio capture');
        throw new Error('Connection lost before starting audio capture');
      }
      
      // Restore original handlers (connection is valid, proceed with audio)
      this.sdk.onError = originalOnError;
      this.sdk.onDisconnected = originalOnDisconnected;
      
      if (this.sdk.isConnected && 
          this.sdk.voiceSDK && 
          this.sdk.voiceSDK.websocket && 
          this.sdk.voiceSDK.websocket.readyState === WebSocket.OPEN) {
        console.log('✅ WebSocket ready, starting listening...');
        
        // Keep status as "connecting" until audio actually starts playing
        // Status will be updated to "listening" when playbackStarted event fires
        
        // CRITICAL: startListening() will request microphone permission
        // This must be called directly from user interaction (button click)
        // to work on mobile browsers
        try {
          await this.sdk.startListening();
          
          // CRITICAL: Check if connection is still valid AFTER startListening completes
          // Server might have rejected during the async startListening() call
          if (!this.sdk.isConnected || 
              !this.sdk.voiceSDK || 
              !this.sdk.voiceSDK.isConnected || 
              !this.sdk.voiceSDK.websocket || 
              this.sdk.voiceSDK.websocket.readyState !== WebSocket.OPEN) {
            // Silent cleanup - server rejected, this is expected
            // Stop recording if it started
            if (this.sdk.voiceSDK?.isRecording) {
              try {
                await this.sdk.voiceSDK.stopRecording();
              } catch (stopError) {
                // Ignore stop errors during cleanup
              }
            }
            const error = new Error('Connection lost - server may have rejected the call');
            error.name = 'ServerRejected';
            error.isServerRejection = true;
            throw error;
          }
          
          console.log('🎤 Started listening - permission granted');
          
          this.isActive = true;
          
          // Update mobile bar if it already exists (created early), otherwise create it
          if (this.isMobile) {
            const existingBar = document.getElementById('mobile-voice-call-bar-container');
            if (!existingBar) {
              console.log('📱 Mobile device - permission granted, creating minimized bar');
              // Panel and floating button are already hidden from permission request step
              // Just create the mobile minimized bar
              this.createMobileMinimizedBar();
            } else {
              console.log('📱 Mobile device - bar already exists, updating status');
            }
            
            // Initialize messages with welcome message
            if (this.messages.length === 0) {
              this.messages.push({ type: 'assistant', text: this.t('transcriptWillAppear') });
            }
            // Start waveform animation
            this.startWaveformAnimation();
            // Keep status as "connecting" until audio actually plays
            // Status will be updated to "listening" when playbackStarted event fires
          } else {
            // Desktop: initialize UI
            this.startDesktopWaveformAnimation();
            // Keep status as "connecting" until audio actually plays
            // Status will be updated to "listening" when playbackStarted event fires
            // Start live waveform animation interval (delegated to desktop module)
            this.desktop.startLiveWaveformInterval();
          }
        } catch (error) {
          // Handle server rejection gracefully (don't log as error)
          if (error.isServerRejection || error.name === 'ServerRejected') {
            // Server rejection is expected - handle it silently
            // The error handler will show domain error message
            this.resetConnectingState();
            // Re-throw so outer catch can handle it appropriately
            throw error;
          }
          
          // Log other errors (permission denied, etc.)
          console.error('❌ Failed to start listening - permission denied or error:', error);
          // Permission was denied or error occurred - reset connecting state and restore UI
          this.resetConnectingState();
          if (this.isMobile) {
            // Remove mobile bar if it was created early
            const existingBar = document.getElementById('mobile-voice-call-bar-container');
            if (existingBar) {
              this.removeMobileMinimizedBar();
            }
            // Restore idle state so user can try again
            const idleState = this.shadowRoot.getElementById('voiceIdleState');
            if (idleState) {
              idleState.style.display = 'flex';
            }
            // Ensure panel is visible
            const panel = this.shadowRoot.getElementById('text-chat-panel');
            if (panel) {
              panel.style.display = '';
            } else {
              const fallbackPanel = document.getElementById('text-chat-panel');
              if (fallbackPanel) {
                fallbackPanel.style.display = '';
              }
            }
            // Ensure floating button is visible
            const floatingButton = this.shadowRoot.getElementById('text-chat-button');
            if (floatingButton) {
              floatingButton.style.display = '';
            } else {
              const fallbackButton = document.getElementById('text-chat-button');
              if (fallbackButton) {
                fallbackButton.style.display = '';
              }
            }
          }
          // Re-throw error so it's handled by outer catch
          throw error;
        }
        
        // Start timer
        this.callStartTime = Date.now();
        console.log('⏱️ Starting call timer at', new Date(this.callStartTime).toISOString());
        
        // Timer update function - updates all timer elements
        const updateTimer = () => {
          if (!this.callStartTime) {
            console.warn('⚠️ Timer update called but callStartTime is not set');
            return;
          }

          const elapsed = Date.now() - this.callStartTime;
          const minutes = Math.floor(elapsed / 60000);
          const seconds = Math.floor((elapsed % 60000) / 1000);
          const timerText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

          this._updateTimerElements(timerText, elapsed);
        };
        
        // Wait a tiny bit for DOM to be ready, then start timer
        setTimeout(() => {
          console.log('⏱️ Starting timer interval');
          // Update immediately (don't wait for first interval)
          updateTimer();
          // Then update every second
          this.callTimerInterval = setInterval(updateTimer, 1000);
        }, 100);
      } else {
        // Check if server rejected before trying to start listening
        if (serverRejected) {
          console.log('🚫 Server rejected connection, aborting audio capture');
          throw new Error('DOMAIN_NOT_WHITELISTED');
        }
        
        // Check if connection is still valid
        if (!this.sdk.isConnected || 
            !this.sdk.voiceSDK || 
            !this.sdk.voiceSDK.isConnected || 
            !this.sdk.voiceSDK.websocket || 
            this.sdk.voiceSDK.websocket.readyState !== WebSocket.OPEN) {
          console.warn('⚠️ Connection not valid, aborting audio capture');
          throw new Error('Connection lost before starting audio capture');
        }
        
        console.warn('⚠️ Connection not fully ready, but trying to start listening anyway...');
        try {
          await this.sdk.startListening();
          console.log('🎤 Started listening - permission granted');
          
          this.isActive = true;
          
          // Update mobile bar if it already exists (created early), otherwise create it
          if (this.isMobile) {
            const existingBar = document.getElementById('mobile-voice-call-bar-container');
            if (!existingBar) {
              console.log('📱 Mobile device - permission granted, creating minimized bar');
              // Panel and floating button are already hidden from permission request step
              // Just create the mobile minimized bar
              this.createMobileMinimizedBar();
            } else {
              console.log('📱 Mobile device - bar already exists, updating status');
            }
            
            // Initialize messages with welcome message
            if (this.messages.length === 0) {
              this.messages.push({ type: 'assistant', text: this.t('transcriptWillAppear') });
            }
            // Start waveform animation
            this.startWaveformAnimation();
            // Keep status as "connecting" until audio actually plays
            // Status will be updated to "listening" when playbackStarted event fires
          } else {
            // Desktop: initialize UI
            this.startDesktopWaveformAnimation();
            // Keep status as "connecting" until audio actually plays
            // Status will be updated to "listening" when playbackStarted event fires
            // Start live waveform animation interval (delegated to desktop module)
            this.desktop.startLiveWaveformInterval();
          }
        } catch (error) {
          // Handle server rejection gracefully (don't log as error)
          if (error.isServerRejection || error.name === 'ServerRejected') {
            // Server rejection is expected - handle it silently
            // The error handler will show domain error message
            this.resetConnectingState();
            // Re-throw so outer catch can handle it appropriately
            throw error;
          }
          
          // Log other errors (permission denied, etc.)
          console.error('❌ Failed to start listening - permission denied or error:', error);
          // Permission was denied or error occurred - reset connecting state and restore UI
          this.resetConnectingState();
          if (this.isMobile) {
            // Remove mobile bar if it was created early
            const existingBar = document.getElementById('mobile-voice-call-bar-container');
            if (existingBar) {
              this.removeMobileMinimizedBar();
            }
            // Restore idle state so user can try again
            const idleState = this.shadowRoot.getElementById('voiceIdleState');
            if (idleState) {
              idleState.style.display = 'flex';
            }
            // Ensure panel is visible
            const panel = this.shadowRoot.getElementById('text-chat-panel');
            if (panel) {
              panel.style.display = '';
            } else {
              const fallbackPanel = document.getElementById('text-chat-panel');
              if (fallbackPanel) {
                fallbackPanel.style.display = '';
              }
            }
            // Ensure floating button is visible
            const floatingButton = this.shadowRoot.getElementById('text-chat-button');
            if (floatingButton) {
              floatingButton.style.display = '';
            } else {
              const fallbackButton = document.getElementById('text-chat-button');
              if (fallbackButton) {
                fallbackButton.style.display = '';
              }
            }
          }
          // Re-throw error so it's handled by outer catch
          throw error;
        }
        
        // Start timer
        this.callStartTime = Date.now();
        console.log('⏱️ Starting call timer at', new Date(this.callStartTime).toISOString());
        
        // Timer update function - updates all timer elements
        const updateTimer = () => {
          if (!this.callStartTime) {
            console.warn('⚠️ Timer update called but callStartTime is not set');
            return;
          }

          const elapsed = Date.now() - this.callStartTime;
          const minutes = Math.floor(elapsed / 60000);
          const seconds = Math.floor((elapsed % 60000) / 1000);
          const timerText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

          this._updateTimerElements(timerText, elapsed);
        };
        
        // Wait a tiny bit for DOM to be ready, then start timer
        setTimeout(() => {
          console.log('⏱️ Starting timer interval');
          // Update immediately (don't wait for first interval)
          updateTimer();
          // Then update every second
          this.callTimerInterval = setInterval(updateTimer, 1000);
        }, 100);
      }
      
      console.log('✅ Voice call started successfully');
      
    } catch (error) {
      // Handle server rejection gracefully (don't log as error)
      if (!(error.isServerRejection || error.name === 'ServerRejected')) {
        console.error('❌ Error starting voice call:', error);
      }
      
      // CRITICAL: Reset connecting state first
      this.resetConnectingState();
      
      // CRITICAL: Reset isActive flag so user can retry
      // This ensures that if permission was denied, the user can click the button again
      this.isActive = false;
      
      // Clean up any partial state
      if (this.callTimerInterval) {
        clearInterval(this.callTimerInterval);
        this.callTimerInterval = null;
      }
      
      // Disconnect SDK if it was partially connected
      if (this.sdk && this.sdk.isConnected) {
        try {
          this.sdk.disconnect();
        } catch (disconnectError) {
          console.warn('Error disconnecting SDK on error:', disconnectError);
        }
      }
      
      // Handle specific error types with appropriate modals
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Permission denied - show blocked modal
        const deviceInfo = getDeviceInfo();
        showBlockedModal(
          () => {
            // Modal closed
            this.resetUIState();
          },
          () => {
            // User clicked refresh
            window.location.reload();
          }
        );
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        // No microphone found - show no mic modal
        showNoMicrophoneModal(() => {
          this.resetUIState();
        });
      } else if (error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
          (error.message && error.message.includes('Domain not whitelisted')))) {
        // Domain not whitelisted - end call and stop audio capture
        await this.endCallOnServerRejection();
      } else {
        // Other errors - show in transcript
        this.showError(error.message || error);
        this.resetUIState();
      }
    }
  }

  /**
   * Reset UI state to idle
   */
  resetUIState() {
    // Reset connecting state first (if it was shown)
    this.resetConnectingState();
    
    // Mobile: Remove minimized bar and restore panel
    if (this.isMobile) {
      this.removeMobileMinimizedBar();
      this.stopWaveformAnimation();
      this.messages = [];
      
      // Restore widget panel visibility
      const panel = this.shadowRoot.getElementById('text-chat-panel');
      if (panel) {
        panel.style.display = '';
      } else {
        const fallbackPanel = document.getElementById('text-chat-panel');
        if (fallbackPanel) {
          fallbackPanel.style.display = '';
        }
      }
    }
    
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (idleState) idleState.style.display = 'flex';
    if (activeState) activeState.style.display = 'none';
  }

  /**
   * End call when server rejects (domain error, etc.)
   * Stops audio capture/VAD immediately and cleans up
   */
  async endCallOnServerRejection() {
    console.log('🚫 Server rejected call - ending call and stopping audio capture/VAD...');
    console.log('🚫 Calling endCallOnServerRejection() - will show domain error message');
    
    // CRITICAL: Ensure voice interface is visible before showing error
    // The voice interface might be hidden if we're on landing screen
    // We need to call the parent widget's showVoice() method if available
    const voiceInterfaceEl = this.shadowRoot.getElementById('voiceInterface');
    const landingScreen = this.shadowRoot.getElementById('landingScreen');
    
    if (voiceInterfaceEl) {
      voiceInterfaceEl.classList.add('active');
      console.log('✅ Made voice interface visible');
    }
    
    if (landingScreen) {
      landingScreen.classList.remove('active');
      console.log('✅ Hid landing screen');
    }
    
    // Also try to call parent widget's showVoice method if available via callback
    if (this.config.onShowVoice && typeof this.config.onShowVoice === 'function') {
      console.log('🚫 Calling onShowVoice callback to ensure voice interface is visible...');
      this.config.onShowVoice();
    }
    
    // CRITICAL: Stop audio capture/VAD immediately
    if (this.sdk) {
      try {
        // CRITICAL: Stop VAD and audio capture immediately
        // Check if AudioRecorder exists and is recording (even if flags aren't set yet)
        const audioRecorder = this.sdk.voiceSDK?.audioRecorder;
        const isRecording = this.sdk.isListening || 
                           this.sdk.voiceSDK?.isRecording || 
                           (audioRecorder && audioRecorder.isRecording) ||
                           (audioRecorder && audioRecorder.audioWorkletNode !== null);
        
        if (isRecording || audioRecorder) {
          console.log('🛑 Stopping audio capture/VAD...');
          
          // First, send stop message to AudioWorklet to stop VAD processing
          if (audioRecorder?.audioWorkletNode) {
            console.log('🛑 Sending stop message to AudioWorklet to stop VAD...');
            try {
              audioRecorder.audioWorkletNode.port.postMessage({ type: 'stop' });
              await new Promise(resolve => setTimeout(resolve, 150)); // Give VAD time to stop processing
            } catch (e) {
              console.warn('Error sending stop message to AudioWorklet:', e);
            }
          }
          
          // Then call stopRecording to stop AudioRecorder and close AudioContext
          if (this.sdk.voiceSDK && typeof this.sdk.voiceSDK.stopRecording === 'function') {
            try {
              await this.sdk.voiceSDK.stopRecording();
              console.log('✅ stopRecording() completed');
            } catch (e) {
              console.warn('Error calling stopRecording:', e);
            }
          } else if (this.sdk.stopListening) {
            // Fallback to stopListening if stopRecording not available
            try {
              this.sdk.stopListening();
              await new Promise(resolve => setTimeout(resolve, 200));
            } catch (e) {
              console.warn('Error calling stopListening:', e);
            }
          }
          
          // Also try stopping AudioRecorder directly if it exists
          if (audioRecorder && typeof audioRecorder.stop === 'function') {
            try {
              await audioRecorder.stop();
              console.log('✅ AudioRecorder.stop() completed');
            } catch (e) {
              console.warn('Error calling AudioRecorder.stop():', e);
            }
          }
          
          console.log('✅ Audio capture/VAD stopped');
        }
        
        // Force stop media stream tracks if they still exist (backup cleanup)
        if (audioRecorder?.mediaStream) {
          console.log('🛑 Force stopping media stream tracks (backup cleanup)...');
          audioRecorder.mediaStream.getTracks().forEach(track => {
            if (track.readyState !== 'ended') {
              track.stop();
              console.log('✅ Force stopped track:', track.kind);
            }
          });
        }
        
        // Also stop any stored audioStream
        if (this.audioStream) {
          console.log('🛑 Stopping stored audio stream...');
          this.audioStream.getTracks().forEach(track => {
            if (track.readyState !== 'ended') {
              track.stop();
            }
          });
          this.audioStream = null;
        }
        
        // Hangup the call
        this.sdk.hangup();
      } catch (error) {
        console.warn('Error stopping listening on server rejection:', error);
        // Force stop media streams even if stopListening fails
        try {
          if (this.sdk.voiceSDK?.audioRecorder?.mediaStream) {
            this.sdk.voiceSDK.audioRecorder.mediaStream.getTracks().forEach(track => track.stop());
          }
          if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
          }
        } catch (forceStopError) {
          console.warn('Error force-stopping media streams:', forceStopError);
        }
      }
      
      // Disconnect SDK
      if (this.sdk.isConnected) {
        try {
          this.sdk.disconnect();
        } catch (disconnectError) {
          console.warn('Error disconnecting SDK on server rejection:', disconnectError);
        }
      }
    }
    
    // Reset state (but keep currentView as 'voice' so we stay on voice interface)
    this.isActive = false;
    
    // CRITICAL: Set currentView to 'voice' to prevent returning to landing screen
    // This ensures the domain error stays visible
    if (this.config.onCallEnd && typeof this.config.onCallEnd === 'function') {
      // Notify parent that we're showing an error (but staying on voice interface)
      // The parent should NOT call showLanding() when domain error is shown
    }
    
    // Stop timer if running
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    
    // Hide active state, show idle state first
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    if (activeState) activeState.style.display = 'none';
    if (idleState) {
      idleState.style.display = 'flex';
      
      // CRITICAL: Set flag BEFORE resetting or showing error
      // This prevents resetConnectingState() from clearing the error
      this.isShowingDomainError = true;
      console.log('🚫 Setting isShowingDomainError flag to true');
      
      // Don't reset connecting state when showing domain error - go straight to showing error
      // resetConnectingState() would clear the error message
      
      // Show domain error message directly
      console.log('🚫 Calling showDomainError() to display domain error in widget...');
      this.showDomainError();
      console.log('🚫 showDomainError() completed, isShowingDomainError:', this.isShowingDomainError);
    }
    
    // Mobile: restore panel visibility
    if (this.isMobile) {
      const panel = this.shadowRoot.getElementById('text-chat-panel');
      if (panel) {
        panel.style.display = '';
      } else {
        const fallbackPanel = document.getElementById('text-chat-panel');
        if (fallbackPanel) {
          fallbackPanel.style.display = '';
        }
      }
      
      // Restore floating button
      const floatingButton = this.shadowRoot.getElementById('text-chat-button');
      if (floatingButton) {
        floatingButton.style.display = '';
      } else {
        const fallbackButton = document.getElementById('text-chat-button');
        if (fallbackButton) {
          fallbackButton.style.display = '';
        }
      }
      
      // Remove mobile minimized bar if it exists
      this.removeMobileMinimizedBar();
      this.stopWaveformAnimation();
    }
  }
  
  /**
   * End voice call
   */
  endVoiceCall() {
    console.log('🔴 Ending voice call...');
    
    // Reset resume info so next call is treated as a new call (not resume)
    // Resume info is only valid for the first call after page load/refresh
    this._resumeInfo = null;
    
    // Stop listening and hangup gracefully
    if (this.sdk && this.isActive) {
      this.sdk.stopListening();
      this.sdk.hangup();
      this.isActive = false;
    }
    
    // Stop audio stream (if any)
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    
    // Stop timer
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    
    // Clean up text inject timeouts and handlers (desktop)
    if (this._textInjectTimeout) {
      clearTimeout(this._textInjectTimeout);
      this._textInjectTimeout = null;
    }

    if (this._statusClearTimeout) {
      clearTimeout(this._statusClearTimeout);
      this._statusClearTimeout = null;
    }

    // Clean up text inject timeouts and handlers (mobile)
    if (this._mobileTextInjectTimeout) {
      clearTimeout(this._mobileTextInjectTimeout);
      this._mobileTextInjectTimeout = null;
    }

    if (this._mobileStatusClearTimeout) {
      clearTimeout(this._mobileStatusClearTimeout);
      this._mobileStatusClearTimeout = null;
    }

    // Remove text inject ack handler (delegated to textHandlers)
    if (this.sdk?.voiceSDK && this.textHandlers._textInjectAckHandler) {
      this.sdk.voiceSDK.off('textInjectAck', this.textHandlers._textInjectAckHandler);
      this.textHandlers._textInjectAckHandler = null;
    }

    // Reset hint text references
    this._originalHintText = null;
    this._originalMobileHintText = null;
    
    // Desktop-specific cleanup
    if (!this.isMobile) {
      this.stopDesktopWaveformAnimation();
      // Stop live waveform interval (delegated to desktop module)
      this.desktop.stopLiveWaveformInterval();
      this.transcript.clearConversationMessages();
      this.conversationExpanded = false;
    }
    
    // Mobile-specific cleanup
    if (this.isMobile) {
      this.stopWaveformAnimation();
      this.collapseMobileTranscript();
      this.removeMobileMinimizedBar();
      this.messages = [];
      
      // Show widget panel again (restore it)
      // Restore widget panel (use shadowRoot if in Shadow DOM)
      const panel = this.shadowRoot.getElementById('text-chat-panel');
      if (panel) {
        panel.style.display = ''; // Remove inline display:none
      } else {
        // Fallback: try document if not in Shadow DOM
        const fallbackPanel = document.getElementById('text-chat-panel');
        if (fallbackPanel) {
          fallbackPanel.style.display = '';
        }
      }
    }
    
    // Restore floating button visibility (it was hidden when call started)
    const floatingButton = this.shadowRoot.getElementById('text-chat-button');
    if (floatingButton) {
      floatingButton.style.display = ''; // Remove inline display:none
      console.log('✅ Floating button restored (call ended)');
    } else {
      // Fallback: try document if not in Shadow DOM
      const fallbackButton = document.getElementById('text-chat-button');
      if (fallbackButton) {
        fallbackButton.style.display = '';
        console.log('✅ Floating button restored (call ended, fallback)');
      }
    }
    
    // Hide active state
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (activeState) activeState.style.display = 'none';
    
    // Call the public callback if provided (for user tracking/analytics)
    if (typeof this.config.onVoiceCallButtonClick === 'function') {
      try {
        this.config.onVoiceCallButtonClick({ timestamp: Date.now(), widgetState: 'end' });
      } catch (error) {
        console.error('Error in onVoiceCallButtonClick callback:', error);
      }
    }

    // Call the internal callback if provided (for unified mode - shows landing screen)
    // Otherwise show idle state (for voice-only mode or backward compatibility)
    if (this.config.onCallEnd) {
      this.config.onCallEnd();
    } else {
      // Fallback: show idle state (for voice-only mode or backward compatibility)
      const idleState = this.shadowRoot.getElementById('voiceIdleState');
      if (idleState) idleState.style.display = 'flex';
      
      // Re-adjust sizes for idle state
      setTimeout(() => this.adjustSizesForContainer(), 50);
    }
    
    // Reset transcript
    const transcriptEl = this.shadowRoot.getElementById('transcriptText');
    if (transcriptEl) {
      transcriptEl.textContent = this.t('transcriptWillAppear');
      transcriptEl.classList.add('empty');
    }
    
    // Reset mobile transcript
    if (this.isMobile) {
      const mobileTranscriptText = document.getElementById('mobileTranscriptText');
      if (mobileTranscriptText) {
        mobileTranscriptText.textContent = this.t('transcriptWillAppear');
      }
    }
    
    // Remove speaking state from avatar
    const avatar = this.shadowRoot.getElementById('voiceAvatarActive');
    if (avatar) {
      avatar.classList.remove('speaking');
    }
    
    console.log('✅ Voice call ended');
  }
  
  /**
   * Show connecting state (between landing screen and active call)
   * This provides user feedback during WebSocket connection and SDK initialization
   */
  showConnectingState() {
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    
    if (idleState) {
      // Update idle state to show connecting message
      const statusTitle = idleState.querySelector('.voice-status-title');
      const statusSubtitle = idleState.querySelector('.voice-status-subtitle');
      const startCallBtn = idleState.querySelector('#startCallBtn');
      
      if (statusTitle) {
        statusTitle.textContent = this.t('connecting');
      }
      if (statusSubtitle) {
        statusSubtitle.textContent = this.t('connectingSubtitle');
      }
      if (startCallBtn) {
        startCallBtn.disabled = true;
        startCallBtn.style.opacity = '0.6';
        startCallBtn.style.cursor = 'not-allowed';
        // Update button text to show connecting state
        const btnText = startCallBtn.querySelector('span');
        if (btnText) {
          // Create spinner element
          const spinner = document.createElement('span');
          spinner.className = 'connecting-spinner';
          // Clear existing content and add spinner + text
          btnText.textContent = '';
          btnText.appendChild(spinner);
          btnText.appendChild(document.createTextNode(' ' + this.t('connecting')));
        }
      }
      
      // Show idle state (which now displays connecting message)
      idleState.style.display = 'flex';
    }
    
    // Ensure active state is hidden
    if (activeState) {
      activeState.style.display = 'none';
    }
  }
  
  /**
   * Reset connecting state back to normal idle state
   * Called when call fails to start or is cancelled
   */
  resetConnectingState() {
    // CRITICAL: Don't reset if we're showing a domain error
    if (this.isShowingDomainError) {
      console.log('🚫 resetConnectingState() called but domain error is displayed - skipping reset');
      return;
    }
    
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    
    if (idleState) {
      // Remove domain error back button if it exists
      const domainErrorBackBtn = idleState.querySelector('#domainErrorBackBtn');
      if (domainErrorBackBtn && domainErrorBackBtn.parentNode) {
        domainErrorBackBtn.parentNode.removeChild(domainErrorBackBtn);
      }
      
      // Restore original idle state text
      const statusTitle = idleState.querySelector('.voice-status-title');
      const statusSubtitle = idleState.querySelector('.voice-status-subtitle');
      const startCallBtn = idleState.querySelector('#startCallBtn');
      
      if (statusTitle) {
        statusTitle.textContent = this.getText('startCallTitle', 'clickToStartCall');
        statusTitle.style.color = ''; // Reset color (may have been set by domain error)
        statusTitle.style.fontSize = ''; // Reset font size
        statusTitle.style.fontWeight = ''; // Reset font weight
        statusTitle.style.marginLeft = ''; // Reset margins
        statusTitle.style.marginRight = ''; // Reset margins
        statusTitle.style.textAlign = ''; // Reset text align
      }
      if (statusSubtitle) {
        statusSubtitle.textContent = this.getText('startCallSubtitle', 'realTimeVoice');
        statusSubtitle.style.color = ''; // Reset color (may have been set by domain error)
        statusSubtitle.style.fontSize = ''; // Reset font size
        statusSubtitle.style.marginLeft = ''; // Reset margins
        statusSubtitle.style.marginRight = ''; // Reset margins
        statusSubtitle.style.marginTop = ''; // Reset margin top
        statusSubtitle.style.textAlign = ''; // Reset text align
        statusSubtitle.style.lineHeight = ''; // Reset line height
      }
      if (startCallBtn) {
        startCallBtn.disabled = false;
        startCallBtn.style.opacity = '';
        startCallBtn.style.cursor = '';
        startCallBtn.style.display = ''; // Reset display (may have been hidden by domain error)
        // Restore button text
        const btnText = startCallBtn.querySelector('span');
        if (btnText) {
          // Remove spinner and restore original text
          btnText.textContent = this.getText('startCallButtonText', 'startCall');
        }
      }
    }
  }
  
  /**
   * Update transcript text
   * Delegates to transcript module
   */
  updateTranscript(text) {
    return this.transcript.updateTranscript(text);
  }

  /**
   * Toggle conversation panel expand/collapse
   * Delegates to transcript module
   */
  toggleConversationPanel() {
    return this.transcript.toggleConversationPanel();
  }

  /**
   * Add message to conversation history
   * Delegates to transcript module
   */
  addConversationMessage(role, content) {
    return this.transcript.addConversationMessage(role, content);
  }

  /**
   * Render conversation messages
   * Delegates to transcript module
   */
  renderConversationMessages() {
    return this.transcript.renderConversationMessages();
  }
  
  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  

  /**
   * Toggle mute state
   */
  toggleMute() {
    if (!this.sdk || !this.isActive) {
      return;
    }
    
    // Access the audio recorder's media stream through VoiceSDK
    let audioTrack = null;
    if (this.sdk.voiceSDK && this.sdk.voiceSDK.audioRecorder && this.sdk.voiceSDK.audioRecorder.mediaStream) {
      audioTrack = this.sdk.voiceSDK.audioRecorder.mediaStream.getAudioTracks()[0];
    }
    
    // Fallback to stored audioStream if available
    if (!audioTrack && this.audioStream) {
      audioTrack = this.audioStream.getAudioTracks()[0];
    }
    
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      this.isMicMuted = !audioTrack.enabled;
      
      // Desktop mute button (both original and compact layouts)
      const muteBtn = this.shadowRoot.getElementById('desktopMuteBtn');
      const muteBtnCompact = this.shadowRoot.getElementById('desktopMuteBtnCompact');
      
      // Update desktop status
      if (!this.isMobile) {
        this.updateDesktopStatus();
      }
      
      // Update original layout button
      if (muteBtn) {
        const micIcon = muteBtn.querySelector('.desktop-mic-icon');
        const micOffIcon = muteBtn.querySelector('.desktop-mic-off-icon');
        if (micIcon && micOffIcon) {
          if (!audioTrack.enabled) {
            muteBtn.classList.add('muted');
            micIcon.style.display = 'none';
            micOffIcon.style.display = 'block';
          } else {
            muteBtn.classList.remove('muted');
            micIcon.style.display = 'block';
            micOffIcon.style.display = 'none';
          }
        }
      }
      
      // Update compact layout button (sync state)
      if (muteBtnCompact) {
        const micIconCompact = muteBtnCompact.querySelector('.desktop-mic-icon');
        const micOffIconCompact = muteBtnCompact.querySelector('.desktop-mic-off-icon');
        if (micIconCompact && micOffIconCompact) {
          if (!audioTrack.enabled) {
            muteBtnCompact.classList.add('muted');
            micIconCompact.style.display = 'none';
            micOffIconCompact.style.display = 'block';
          } else {
            muteBtnCompact.classList.remove('muted');
            micIconCompact.style.display = 'block';
            micOffIconCompact.style.display = 'none';
          }
        }
      }
      
      // Mobile mute button
      if (this.isMobile) {
        const mobileMuteBtn = document.getElementById('mobileMuteBtn');
        const mobileMicIcon = mobileMuteBtn?.querySelector('.mobile-mic-icon');
        const mobileMicOffIcon = mobileMuteBtn?.querySelector('.mobile-mic-off-icon');
        
        if (mobileMuteBtn && mobileMicIcon && mobileMicOffIcon) {
          if (!audioTrack.enabled) {
            // Muted: hide white icon, show red icon
            mobileMuteBtn.classList.add('muted');
            mobileMicIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
            mobileMicOffIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
          } else {
            // Unmuted: show white icon, hide red icon
            mobileMuteBtn.classList.remove('muted');
            mobileMicIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
            mobileMicOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
          }
        }
      }
      
      console.log(audioTrack.enabled ? '🔊 Unmuted' : '🔇 Muted');
    } else {
      console.warn('⚠️ No audio track available for mute toggle');
    }
  }

  /**
   * Toggle speaker (placeholder)
   */
  toggleSpeaker() {
    if (!this.sdk || !this.isActive) {
      return;
    }
    
    // Toggle speaker mute state
    this.isSpeakerMuted = !this.isSpeakerMuted;
    
    // Control audio playback volume through AudioPlayer
    if (this.sdk.voiceSDK && this.sdk.voiceSDK.audioPlayer) {
      this.sdk.voiceSDK.audioPlayer.setMuted(this.isSpeakerMuted);
      console.log('🔊 Speaker toggle:', this.isSpeakerMuted ? 'muted' : 'unmuted');
    } else {
      console.warn('⚠️ Cannot toggle speaker - audioPlayer not available');
    }
    
    // Update desktop speaker button (both original and compact layouts)
    if (!this.isMobile) {
      const speakerBtn = this.shadowRoot.getElementById('desktopSpeakerBtn');
      const speakerBtnCompact = this.shadowRoot.getElementById('desktopSpeakerBtnCompact');
      
      // Update original layout button
      if (speakerBtn) {
        const speakerIcon = speakerBtn.querySelector('.desktop-speaker-icon');
        const speakerOffIcon = speakerBtn.querySelector('.desktop-speaker-off-icon');
        if (speakerIcon && speakerOffIcon) {
          if (this.isSpeakerMuted) {
            speakerBtn.classList.add('muted');
            speakerIcon.style.display = 'none';
            speakerOffIcon.style.display = 'block';
          } else {
            speakerBtn.classList.remove('muted');
            speakerIcon.style.display = 'block';
            speakerOffIcon.style.display = 'none';
          }
        }
      }
      
      // Update compact layout button (sync state)
      if (speakerBtnCompact) {
        const speakerIconCompact = speakerBtnCompact.querySelector('.desktop-speaker-icon');
        const speakerOffIconCompact = speakerBtnCompact.querySelector('.desktop-speaker-off-icon');
        if (speakerIconCompact && speakerOffIconCompact) {
          if (this.isSpeakerMuted) {
            speakerBtnCompact.classList.add('muted');
            speakerIconCompact.style.display = 'none';
            speakerOffIconCompact.style.display = 'block';
          } else {
            speakerBtnCompact.classList.remove('muted');
            speakerIconCompact.style.display = 'block';
            speakerOffIconCompact.style.display = 'none';
          }
        }
      }
    }
    
    // Update mobile speaker button
    if (this.isMobile) {
      const mobileSpeakerBtn = document.getElementById('mobileSpeakerBtn');
      const mobileSpeakerIcon = mobileSpeakerBtn?.querySelector('.mobile-speaker-icon');
      const mobileSpeakerOffIcon = mobileSpeakerBtn?.querySelector('.mobile-speaker-off-icon');
      
      if (mobileSpeakerBtn && mobileSpeakerIcon && mobileSpeakerOffIcon) {
        if (this.isSpeakerMuted) {
          // Muted: hide white icon, show red icon
          mobileSpeakerBtn.classList.add('muted');
          mobileSpeakerIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
          mobileSpeakerOffIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
        } else {
          // Unmuted: show white icon, hide red icon
          mobileSpeakerBtn.classList.remove('muted');
          mobileSpeakerIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
          mobileSpeakerOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
        }
      }
    }
  }

  /**
   * Update mobile status indicator
   * Delegates to mobile module
   */
  updateMobileStatus(status) {
    return this.mobile.updateMobileStatus(status);
  }

  /**
   * Create mobile minimized bar
   * Delegates to mobile module
   */
  createMobileMinimizedBar() {
    return this.mobile.createMobileMinimizedBar();
  }

  /**
   * Remove mobile minimized bar
   * Delegates to mobile module
   */
  removeMobileMinimizedBar() {
    return this.mobile.removeMobileMinimizedBar();
  }

  /**
   * Expand mobile transcript
   * Delegates to mobile module
   */
  expandMobileTranscript() {
    return this.mobile.expandMobileTranscript();
  }

  /**
   * Collapse mobile transcript
   * Delegates to mobile module
   */
  collapseMobileTranscript() {
    return this.mobile.collapseMobileTranscript();
  }

  /**
   * Add mobile message
   * Delegates to mobile module
   */
  addMobileMessage(type, text) {
    return this.mobile.addMobileMessage(type, text);
  }

  /**
   * Start mobile waveform animation
   * Delegates to mobile module
   */
  startWaveformAnimation() {
    return this.mobile.startWaveformAnimation();
  }

  /**
   * Stop mobile waveform animation
   * Delegates to mobile module
   */
  stopWaveformAnimation() {
    return this.mobile.stopWaveformAnimation();
  }

  /**
   * Cleanup resources
   */
  destroy() {
    // End call if active
    if (this.isActive) {
      this.endVoiceCall();
    }
    
    // Clean up voice call resources
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    
    // Disconnect SDK
    if (this.sdk) {
      this.sdk.disconnect();
      this.sdk = null;
    }
  }
}